import React, { useMemo, useEffect, useRef } from 'react'
import { makeStyles } from '@mui/styles'
import { IconButton, TextareaAutosize } from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import MicIcon from '@mui/icons-material/Mic'
import StopCircleIcon from '@mui/icons-material/StopCircle'

import AiChatBubble from './ai-chat-bubble'
import AiChatAnswer from './ai-chat-answer'
import {
  sendMessage,
  setInputValue,
  selectMessages,
  selectInputValue,
  selectSendMessageOption,
  SendMessageOption,
  // TODO: Add file upload when implemented in api
  // selectAttachments,
  setAttachments,
  selectCurrentConversationId,
  getConversation,
  selectCurrentConversation,
  updateConversation,
  selectConversations,
  ConversationStatus,
  ChatMessageRole,
  selectSendAfterMicInput,
} from '@/redux/ducks/ai/chat'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import lang from '@/lang'
import { InputLoader } from '@/modules/base/components/inputs'
import { useBrowserSTT } from '@/hooks/useBrowserSTT'
import { toast } from '@/modules/base/components/toast/toastConfig'

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
  },
  scrollContainer: {
    position: 'relative',
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    minHeight: 0,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    padding: '15px 20px',
  },
  footer: {
    display: 'flex',
    gap: 10,
    flexShrink: 0,
    position: 'sticky',
    bottom: 0,
    padding: 15,
    borderTop: theme.border,
    [theme.devices.mobile]: {
      position: 'sticky',
    },
  },
  empty: {
    padding: '20px 0px',
  },
  actionArea: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 8,
  },
  input: {
    width: '100%',
    border: 'none',
    '&:focus': {
      outline: 'none',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
      background: 'inherit',
    },
  },
  inputLoader: {
    width: 20,
    height: 20,
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: 15,
  },
  attachmentArea: {
    display: 'flex',
    alignItems: 'center',
    gap: 15,
  },
  // TODO: Add file upload when implemented in api
  // attachments: {
  //   display: 'flex',
  //   gap: 5,
  //   flexWrap: 'wrap',
  // },
  // attachmentChip: {
  //   '& .MuiChip-deleteIcon': {
  //     fontSize: 14,
  //     color: theme.textMuted,
  //     '&:hover': {
  //       color: theme.error,
  //     },
  //   },
  // },
}))

export default function AiChatContent() {
  const classes = useStyles()
  const dispatch = useAppDispatch()
  const contentEndRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  // TODO: Add file upload when implemented in api
  // const fileInputRef = useRef<HTMLInputElement>(null)

  const messages = useAppSelector((state) => selectMessages(state))
  const inputValue = useAppSelector((state) => selectInputValue(state))
  const sendMessageOption = useAppSelector((state) => selectSendMessageOption(state))
  // TODO: Add file upload when implemented in api
  // const attachments = useAppSelector((state) => selectAttachments(state))
  const currentConversationId = useAppSelector((state) => selectCurrentConversationId(state))
  const currentConversation = useAppSelector((state) => selectCurrentConversation(state))
  const conversations = useAppSelector((state) => selectConversations(state))
  const sendAfterMicInput = useAppSelector((state) => selectSendAfterMicInput(state))

  const isPending = useMemo(() => {
    return currentConversation?.status === ConversationStatus.PENDING
  }, [currentConversation?.status])

  const stt = useBrowserSTT(
    {
      lang: 'de-DE',
      interimResults: true,
      continuous: false,
      maxAlternatives: 1,
      enableKeyboardShortcut: !isPending,
    },
    {
      onFinal: (text) => {
        const next
          = (inputValue ? (inputValue.endsWith(' ') ? inputValue : inputValue + ' ') : '')
          + text
        if (sendAfterMicInput) {
          handleSendMessage(next)
        } else {
          dispatch(setInputValue(next))
        }
      },
      onError: (err) => {
        let errorMessage = lang.ai.chat.errors.voice.default
        if (err === 'not-allowed') errorMessage = lang.ai.chat.errors.voice.notAllowed
        if (err === 'not-supported') errorMessage = lang.ai.chat.errors.voice.notSupported
        toast.showToast({ type: 'error', message: errorMessage })
      },
    },
  )

  useEffect(() => {
    if (!currentConversationId) {
      return
    }
    // skip if this is a newly created conversation (data is already local)
    if (currentConversation?.isNew) {
      dispatch(updateConversation({ isNew: false }))
      return
    }

    const localConversation = conversations.find(t => t.id === currentConversationId)
    if (localConversation && localConversation.messages?.length > 0) {
      return
    }

    dispatch(getConversation({ conversationId: currentConversationId }))
  }, [currentConversationId, currentConversation?.isNew, dispatch])

  useEffect(() => {
    if (contentEndRef.current && messages.length > 0) {
      setTimeout(() => {
        contentEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }, [messages])

  const placeholder = useMemo(() => {
    if (sendMessageOption === SendMessageOption.ENTER) {
      return lang.ai.chat.input.placeholder
    }
    return lang.ai.chat.input.placeholderCtrlEnter
  }, [sendMessageOption])

  // stop STT if conversation is pending
  useEffect(() => {
    if (isPending && stt.listening) stt.stop()
  }, [isPending, stt])

  const handleSendMessage = (next?: string) => {
    if ((!next?.trim() && !inputValue.trim()) || isPending) { return }
    dispatch(sendMessage({ conversationId: currentConversationId, message: next || inputValue }))
    dispatch(setAttachments([]))
    setTimeout(() => {
      contentEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      textareaRef.current?.focus()
    }, 100)
  }

  const handleInputChange = (value: string) => {
    dispatch(setInputValue(value))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const enterKeyPressed = e.key === 'Enter'
    if ((sendMessageOption === SendMessageOption.ENTER && enterKeyPressed && !e.ctrlKey) || (sendMessageOption === SendMessageOption.ENTER_CTRL && enterKeyPressed && e.ctrlKey)) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleVoiceInput = () => {
    if (!stt.supported) {
      return toast.showToast({ type: 'error', message: lang.ai.chat.errors.voice.notSupported })
    }
    stt.toggle()
  }

  // TODO: Add file upload when implemented in api
  // const handleFile = () => {
  //   fileInputRef.current?.click()
  // }
  // const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const files = e.target.files
  //   if (files && files.length > 0) {
  //     dispatch(setAttachments([...attachments, ...Array.from(files)]))
  //   }
  // }
  // const handleFileRemove = (index: number) => {
  //   dispatch(setAttachments(attachments.filter((_, i) => i !== index)))
  // }

  const content = useMemo(() => {
    if (!messages || messages?.length === 0) {
      return (
        <div className={classes.empty}>
          <AiChatAnswer
            message={{ id: 'empty', content: '', role: ChatMessageRole.ASSISTANT, createdAt: Date.now() }} />
        </div>
      )
    }

    return (
      <>
        {messages?.map((message) => {
          if (message.role === ChatMessageRole.USER) {
            return <AiChatBubble key={message.id} message={message.content} />
          }
          return <AiChatAnswer key={message.id} message={message} />
        })}
        {isPending && (
          <AiChatAnswer message={{ id: 'loading', content: '', role: ChatMessageRole.ASSISTANT, createdAt: Date.now(), updatedAt: Date.now() }} />
        )}
        {currentConversation?.status === ConversationStatus.FAILED && (
          <AiChatAnswer message={{ id: 'error', content: lang.ai.chat.errors.default, role: ChatMessageRole.ASSISTANT, createdAt: Date.now(), updatedAt: Date.now() }} />
        )}
        <div ref={contentEndRef} />
      </>
    )
  }, [currentConversation, messages])

  return (
    <div className={classes.container}>
      <div className={classes.scrollContainer}>
        <div className={classes.content}>
          {content}
        </div>
      </div>
      <div className={classes.footer}>
        <TextareaAutosize
          maxRows={5}
          minRows={1}
          ref={textareaRef}
          className={classes.input}
          id="ai-chat-input"
          data-qs-id="ai-chat-input"
          name="ai-chat-input"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e)}
          disabled={stt.listening}
        />
        <div className={classes.actionArea}>
          <div className={classes.attachmentArea}>
            {/* TODO: Add file upload when implemented in api */}
            {/* <IconButton
              size="small"
              data-qs-id="ai-chat-button-file-upload"
              disabled={isPending || stt.listening}
              onClick={handleFile}
              title={lang.ai.chat.attachment}
            >
              <AttachFileIcon fontSize="medium" />
            </IconButton>
            <div className={classes.attachments}>
              {attachments.map((attachment, index) => {
                return (
                  <Chip
                    color="primary"
                    variant="outlined"
                    size="small"
                    className={classes.attachmentChip}
                    label={attachment.name}
                    key={`attachment-${index}`}
                    onDelete={() => handleFileRemove(index)}
                    data-qs-id={`ai-chat-attachment-${index}`}
                    deleteIcon={<ClearIcon fontSize="small" />}
                  />
                )
              })}
            </div>
            <input
              type="file"
              multiple
              style={{ display: 'none' }}
              ref={fileInputRef}
              onChange={handleFileChange}
            /> */}
          </div>
          <div className={classes.actionButtons}>
            {stt.supported && (
              <IconButton
                size="small"
                disabled={isPending}
                onClick={handleVoiceInput}
                title={lang.ai.chat.button}
                data-qs-id="ai-chat-button-voice-input"
              >
                {stt.listening ? <StopCircleIcon fontSize="medium" /> : <MicIcon fontSize="medium" />}
              </IconButton>
            )}
            <IconButton
              size="small"
              disabled={isPending || stt.listening || !inputValue.trim()}
              onClick={() => handleSendMessage()}
              title={lang.ai.chat.button}
              data-qs-id="ai-chat-button-send-message"
            >
              {isPending ? <InputLoader customClass={classes.inputLoader} dataQsId="ai-chat-input-send-message-loader" /> : <SendIcon fontSize="medium" />}
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  )
}
